import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import Card from '../UI/Card';
import Button from '../UI/Button';
import Badge from '../UI/Badge';
import Input from '../UI/Input';

export default function BoardList() {
  const navigate = useNavigate();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({
    category: '',
    search: '',
    page: 1,
  });
  const [pagination, setPagination] = useState({});

  const categories = [
    { value: '', label: '전체' },
    { value: 'COLLABORATION', label: '협업 제안' },
    { value: 'JOINT_CLASS', label: '공동 수업' },
    { value: 'RESOURCE_SHARE', label: '자료 공유' },
    { value: 'QUESTION', label: '질문' },
    { value: 'ANNOUNCEMENT', label: '공지사항' },
  ];

  useEffect(() => {
    fetchPosts();
  }, [filters]);

  const fetchPosts = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (filters.category) params.append('category', filters.category);
      if (filters.search) params.append('search', filters.search);
      params.append('page', filters.page);

      const response = await api.get(`/board?${params.toString()}`);
      setPosts(response.data.posts || []);
      setPagination(response.data.pagination || {});
    } catch (err) {
      setError(err.response?.data?.error || '게시글 목록을 불러올 수 없습니다.');
    } finally {
      setLoading(false);
    }
  };

  const handleCategoryChange = (category) => {
    setFilters(prev => ({
      ...prev,
      category,
      page: 1
    }));
  };

  const handleSearchChange = (e) => {
    setFilters(prev => ({
      ...prev,
      search: e.target.value
    }));
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    setFilters(prev => ({ ...prev, page: 1 }));
  };

  const getCategoryInfo = (category) => {
    const info = {
      COLLABORATION: { label: '협업 제안', variant: 'primary' },
      JOINT_CLASS: { label: '공동 수업', variant: 'secondary' },
      RESOURCE_SHARE: { label: '자료 공유', variant: 'accent' },
      QUESTION: { label: '질문', variant: 'warning' },
      ANNOUNCEMENT: { label: '공지사항', variant: 'danger' },
    };
    return info[category] || { label: category, variant: 'default' };
  };

  if (loading && filters.page === 1) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-neutral-800">교사 협업 게시판</h2>
        <Button onClick={() => navigate('/board/create')}>
          게시글 작성
        </Button>
      </div>

      {/* Category Filter */}
      <div className="flex gap-2 overflow-x-auto pb-2">
        {categories.map((cat) => (
          <button
            key={cat.value}
            onClick={() => handleCategoryChange(cat.value)}
            className={`px-4 py-2 rounded-xl font-medium whitespace-nowrap transition-colors ${
              filters.category === cat.value
                ? 'bg-primary-500 text-white'
                : 'bg-neutral-100 text-neutral-700 hover:bg-neutral-200'
            }`}
          >
            {cat.label}
          </button>
        ))}
      </div>

      {/* Search */}
      <form onSubmit={handleSearchSubmit}>
        <Input
          type="search"
          placeholder="게시글 검색..."
          value={filters.search}
          onChange={handleSearchChange}
        />
      </form>

      {/* Posts */}
      {error ? (
        <Card className="text-center py-8">
          <p className="text-danger-500 mb-4">{error}</p>
          <Button onClick={fetchPosts}>다시 시도</Button>
        </Card>
      ) : posts.length === 0 ? (
        <Card className="text-center py-12">
          <p className="text-neutral-500 mb-4">게시글이 없습니다.</p>
          <Button onClick={() => navigate('/board/create')}>
            첫 게시글 작성하기
          </Button>
        </Card>
      ) : (
        <>
          <div className="space-y-3">
            {posts.map((post) => {
              const catInfo = getCategoryInfo(post.category);
              return (
                <Card
                  key={post.id}
                  hover
                  className="cursor-pointer animate-fade-in"
                  onClick={() => navigate(`/board/${post.id}`)}
                >
                  <div className="flex items-start gap-4">
                    <Badge variant={catInfo.variant}>{catInfo.label}</Badge>

                    <div className="flex-1 min-w-0">
                      <h3 className="font-bold text-neutral-800 mb-1 truncate">
                        {post.title}
                      </h3>
                      <p className="text-sm text-neutral-600 mb-2 line-clamp-2">
                        {post.content}
                      </p>
                      <div className="flex items-center gap-4 text-sm text-neutral-500">
                        <span>{post.author?.username || '알 수 없음'}</span>
                        {post.author?.schoolName && (
                          <span>{post.author.schoolName}</span>
                        )}
                        <span>조회 {post.viewCount || 0}</span>
                        <span>댓글 {post._count?.comments || 0}</span>
                        <span>{new Date(post.createdAt).toLocaleDateString('ko-KR')}</span>
                      </div>
                    </div>
                  </div>
                </Card>
              );
            })}
          </div>

          {/* Pagination */}
          {pagination.totalPages > 1 && (
            <div className="flex justify-center gap-2">
              <Button
                variant="outline"
                size="sm"
                disabled={filters.page === 1}
                onClick={() => setFilters(prev => ({ ...prev, page: prev.page - 1 }))}
              >
                이전
              </Button>
              <span className="flex items-center px-4 text-neutral-600">
                {filters.page} / {pagination.totalPages}
              </span>
              <Button
                variant="outline"
                size="sm"
                disabled={filters.page === pagination.totalPages}
                onClick={() => setFilters(prev => ({ ...prev, page: prev.page + 1 }))}
              >
                다음
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
